import { inject, Injectable } from '@angular/core';
import { Analytics, logEvent } from '@angular/fire/analytics';
import { Title } from '@angular/platform-browser';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { routerNavigatedAction } from '@ngrx/router-store';
import { Action } from '@ngrx/store';
import { filter, tap } from 'rxjs';

import { AnalyticsConsentService } from '../services/analytics-consent.service';

const SIGNAL_ACTION_TYPE_PATTERN = /^\[(?<feature>[^\]]+)\] \[SIGNAL\] (?<name>.+)$/;

type SignalActionMetadata = {
  feature: string;
  name: string;
};

function parseSignalActionType(actionType: string): SignalActionMetadata | null {
  const match = SIGNAL_ACTION_TYPE_PATTERN.exec(actionType);

  if (!match?.groups) {
    return null;
  }

  return {
    feature: match.groups.feature,
    name: match.groups.name,
  };
}

@Injectable()
export class AppAnalyticsEffects {
  private actions$ = inject(Actions);
  private analytics = inject(Analytics, { optional: true });
  private title = inject(Title);
  private consent = inject(AnalyticsConsentService);

  trackPageViews$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(routerNavigatedAction),
        tap(() => {
          if (!this.consent.isGranted || !this.analytics || typeof window === 'undefined') {
            return;
          }

          void logEvent(this.analytics, 'page_view', {
            page_path: `${window.location.pathname}${window.location.search}`,
            page_title: this.title.getTitle(),
            page_location: window.location.href,
          });
        }),
      ),
    { dispatch: false },
  );

  trackSignalActions$ = createEffect(
    () =>
      this.actions$.pipe(
        filter((action: Action) => SIGNAL_ACTION_TYPE_PATTERN.test(action.type)),
        tap((action) => {
          if (!this.consent.isGranted || !this.analytics || typeof window === 'undefined') {
            return;
          }

          const signal = parseSignalActionType(action.type);

          if (!signal) {
            return;
          }

          void logEvent(this.analytics, 'ngrx_signal', {
            signal_feature: signal.feature,
            signal_name: signal.name,
            page_path: `${window.location.pathname}${window.location.search}`,
          });
        }),
      ),
    { dispatch: false },
  );
}
